import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useGetAirportsBySearchParamQuery } from '@massilia/queries';
import { AutoComplete, useDebounce, useIsMobile } from '@massilia/ui';
import { cn } from '@massilia/ui';
import { useStore } from '@tanstack/react-form';
import { MapPin, PlaneLanding, PlaneTakeoff } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFieldContext } from '../hooks/use-app-form.js';
import { DEFAULT_OPTION } from '../schemas/estimate/base-schemas.js';
import { FieldError } from './field-error';
export function AirportAutocompleteField({ autoFocus, errorsDisplayType = 'popover', label, mode, size = 'default', }) {
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 200);
    const { data: searchedOptions } = useGetAirportsBySearchParamQuery({
        searchParam: debouncedSearchText || ' ',
    });
    const options = useMemo(() => {
        if (!searchedOptions)
            return [];
        const options = [];
        searchedOptions.groupedByCitiesAirports.forEach((cityGroup) => {
            options.push({
                category: 'city',
                icon: _jsx(MapPin, { className: "h-4 w-4 text-gray-500" }),
                key: `city-${cityGroup.iataCityCode}`,
                label: `${cityGroup.cityName} (Tous)`,
                value: cityGroup.iataCityCode,
            });
            cityGroup.airports.forEach((airport, index) => {
                if (airport.iataCode) {
                    const isLastChild = index === cityGroup.airports.length - 1;
                    options.push({
                        category: 'airport',
                        icon: mode === 'departure' ? (_jsx(PlaneTakeoff, { className: "h-4 w-4 text-gray-500" })) : (_jsx(PlaneLanding, { className: "h-4 w-4 text-gray-500" })),
                        isChild: true,
                        isLastChild,
                        key: `airport-${airport.iataCode}`,
                        label: `${cityGroup.cityName} ${airport.name} (${airport.iataCode})`,
                        value: airport.iataCode,
                    });
                }
            });
        });
        return options;
    }, [searchedOptions, mode]);
    const field = useFieldContext();
    const errors = useStore(field.store, (state) => state.meta.errors);
    const isMobile = useIsMobile();
    const selectedOption = useMemo(() => {
        const currentValue = field.state.value?.value;
        if (!currentValue)
            return undefined;
        const foundOption = options.find((opt) => opt.value === currentValue);
        if (foundOption)
            return foundOption;
        if (field.state.value?.label) {
            return field.state.value;
        }
        return undefined;
    }, [options, field.state.value]);
    return (_jsxs("div", { className: "relative flex flex-col gap-2", children: [label && _jsx("label", { className: "text-sm font-medium", children: label }), errorsDisplayType === 'popover' && _jsx(FieldError, { asPopover: true, errors: errors }), _jsx(AutoComplete, { autoFocus: autoFocus, className: cn('flex-1', { 'w-74': !isMobile && size === 'compact' }), emptyMessage: "Aucun r\u00E9sultat trouv\u00E9", icon: mode === 'departure' ? (_jsx(PlaneTakeoff, { className: "text-gray-light-700 h-4 w-4" })) : (_jsx(PlaneLanding, { className: "text-gray-light-700 h-4 w-4" })), onBlurCallBack: () => {
                    field.handleBlur();
                }, onInputChange: (text) => {
                    setSearchText(text);
                }, onValueChange: (opt) => {
                    if (opt) {
                        // Only keep serializable data fields, strip rendering metadata (icon, isChild, etc.)
                        field.handleChange({ category: opt.category, label: opt.label, value: opt.value });
                    }
                    else {
                        field.handleChange(DEFAULT_OPTION);
                    }
                }, options: options, placeholder: mode === 'departure' ? 'Lieu de départ' : 'Destination', shouldFilter: false, value: selectedOption }), errorsDisplayType === 'bellow' && _jsx(FieldError, { errors: errors })] }));
}
